import { captureException, captureMessage, withScope } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Globe } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { CheckoutPaymentCard } from "@/components/Portal/DeviceManagement/Roaming/components/CheckoutPaymentCard";
import { RoamingBookingPage } from "@/components/Portal/DeviceManagement/Roaming/components/RoamingBookingPage";
import {
  getHeader,
  isYearlyRoamingPackage,
  optionBasketAtom,
} from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  DeductionChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { LightboxWrapper } from "@/components/Portal/Documents/PaymentMethods/LightboxWrapper/LightboxWrapper";
import { useAlert } from "@/hooks/useAlert";
import { useBasketRefreshOnError } from "@/hooks/useBasketRefreshOnError";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useHandleError } from "@/hooks/useHandleError";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { ErrorType } from "@/mutator/frontendApiInstance";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import {
  orderRoamingPackage,
  useAddOptionToContract,
  useCreateTransaction,
  useGetAvailableSunriseRoamingPackages,
  usePayTransaction,
} from "@/services/api";
import {
  CreateTransactionDatatransOrderType,
  DatatransPaymentResponse,
  DatatransPaymentResponseStatus,
  DrCreditCardCardType,
  GetAvailableSunriseRoamingPackagesPackageType,
  SignUpResponse,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import { getFlatNameFromDevice, stripFlatName } from "@/utils/deviceUtils";
import {
  get3DsBrowserInfo,
  // TODO: Comment back in during 1393
  // isCardholderNameMissing,
} from "@/utils/lightboxHelpers";
import {
  capitalizeFirstLetter,
  getCurrency,
  getCurrencyFromLookupValue,
} from "@/utils/translationHelpers";

type FormProps = {
  paymentProvider: DrCreditCardCardType;
};

/**
 * Checkout step for a selected roaming package.
 *
 * Renders the order summary, payment method card, and price breakdown.
 * Submits via Datatrans lightbox (3DS), direct credit-card charge, or invoice.
 * Reads the pending package basket from `optionBasketAtom` (localStorage-backed).
 * Clears the atom and navigates to `../display` on success or cancellation.
 * Redirects to `../booking` when the atom is stale (package no longer in the available list)
 * or when the package list fails to load.
 */
export const CheckoutRoamingOption: FC = () => {
  const [optionBasket, setOptionBasket] = useAtom(optionBasketAtom);
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const [simId] = useSimIdFromQueryParam();
  const [paymentStatus] = useQueryParam("payment");
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.roaming.checkout",
  });
  const showAlert = useAlert();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const navigate = useNavigate();

  const { data: simDetail } = useDisplayedSim();
  const {
    data: availableRoamingPackages,
    isError: isPackagesError,
    isLoading: isLoadingPackages,
  } = useGetAvailableSunriseRoamingPackages(Number(simId), {
    packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_Roaming,
  });

  const entry = optionBasket?.entries[0];
  const totalOnceAmount = optionBasket?.total?.oneTime?.amount;
  const option = entry?.options?.length ? entry?.options[0] : undefined;
  const optionId = option?.product?.id ?? -1;

  const selectedPackage =
    availableRoamingPackages?.subscriberPackagesRoaming &&
    availableRoamingPackages.subscriberPackagesRoaming.find((pack) => {
      return pack.id === optionId;
    });

  const isYearlyPackage = isYearlyRoamingPackage(
    selectedPackage?.minContractDur,
    selectedPackage?.minContractDurUnit,
  );

  const packageName = stripFlatName(selectedPackage?.shortName);

  const handleSuccess = useCallback(() => {
    setOptionBasket(undefined);
    // Added in order to trigger a refetching in the DeviceManagement
    queryClient.invalidateQueries(["/sim/roamingInfo"]).then(() => {
      showAlert({
        type: AlertTypes.success,
        text: t("cockpit.managementTile.roaming.checkout.success"),
      });
      navigate(`../display?simId=${simId}`, { replace: true });
    });
  }, [navigate, queryClient, setOptionBasket, showAlert, simId, t]);

  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (!isSubmittingCheckout && isSuccess) {
      handleSuccess();
    }
  }, [isSuccess, isSubmittingCheckout, handleSuccess]);

  useEffect(() => {
    if (isPackagesError) {
      handleError("Failed to load roaming packages");
      navigate(`../booking?simId=${simId}`, { replace: true });
    }
  }, [isPackagesError, handleError, navigate, simId]);

  useEffect(() => {
    if (!isLoadingPackages && availableRoamingPackages && !selectedPackage) {
      setOptionBasket(undefined);
      navigate(`../booking?simId=${simId}`, { replace: true });
    }
  }, [
    isLoadingPackages,
    availableRoamingPackages,
    selectedPackage,
    setOptionBasket,
    navigate,
    simId,
  ]);

  const goToPackageSelection = useCallback(() => {
    navigate(`../booking?simId=${simId}`, { replace: true });
  }, [navigate, simId]);

  const refreshOptionBasket = useCallback(() => {
    orderRoamingPackage(Number(simId), { optionId })
      .then((data) => {
        if (!data?.basket) {
          throw new Error("Empty basket response from orderRoamingPackage");
        }

        setOptionBasket(data.basket);
      })
      .catch((error) => {
        handleError(error);
        goToPackageSelection();
      });
  }, [goToPackageSelection, handleError, optionId, setOptionBasket, simId]);

  const handleRefreshableBasketError = useBasketRefreshOnError({
    basketId: optionBasket?.id,
    refreshBasket: refreshOptionBasket,
  });

  const {
    mutate: createTransaction,
    data: datatransRequestDocument,
    isLoading: isCreatingTransaction,
  } = useCreateTransaction({
    mutation: {
      onError: (err) => {
        if (!handleRefreshableBasketError(err)) {
          handleError(err);
        }
      },
    },
  });

  // Both usePayTransaction and useAddOptionToContract share the same behavior onSuccess and onError
  const transactionMutationOptions = {
    onSuccess: (data: DatatransPaymentResponse | SignUpResponse) => {
      // Hide full screen loading
      setFullScreenLoading(false);
      if ("signUpId" in data) {
        handleSuccess();
      } else {
        if (data.status === DatatransPaymentResponseStatus.OK) {
          handleSuccess();
        } else {
          setIsSubmittingCheckout(false);
          handleError(
            "Payment failure",
            `${t("Error.payment.fail")} ${t("Error.payment.pleaseCheckData")}`,
          );
          withScope(function (scope) {
            scope.setFingerprint(["/contract/serviceId/options"]);
            scope.setTag("section", "portal");
            scope.setTag("domain", "CheckoutRoamingOption");
            scope.setTag("endpoint", "addOptionToContract");
            scope.setExtra("simId", simId);
            scope.setContext("paymentContext", {
              isInvoice,
              isCreditCard,
              maskedCardNumberString,
              cardType,
            });
            captureMessage("Payment failure");
          });
        }
      }
    },
    onError: (err: ErrorType<unknown>) => {
      setIsSubmittingCheckout(false);
      // Hide full screen loading
      setFullScreenLoading(false);

      const isRecoveredBasketError = handleRefreshableBasketError(err);

      if (!isRecoveredBasketError) {
        handleError(err);
      }

      // If it fails we report the error in sentry
      withScope(function (scope) {
        scope.setFingerprint(["/contract/serviceId/options"]);
        scope.setTag("section", "portal");
        scope.setTag("domain", "CheckoutRoamingOption");
        scope.setTag("endpoint", "addOptionToContract");
        scope.setTag("basketErrorRecovered", isRecoveredBasketError);
        scope.setExtra("simId", simId);
        scope.setExtra("basket", optionBasket);
        scope.setContext("paymentContext", {
          isInvoice,
          isCreditCard,
          maskedCardNumberString,
          cardType,
        });
        captureException(err);
      });
    },
  };

  // This is used to pay transactions with a saved credit card
  const { mutate: payTransaction } = usePayTransaction({
    mutation: transactionMutationOptions,
  });

  // This is used to add the option to the contract after the payment
  // with a saved credit card or with invoice
  const {
    mutate: addOptionToContract,
    isLoading: isAddingOptionLoading,
    isSuccess: isAddingOptionDone,
  } = useAddOptionToContract({
    mutation: transactionMutationOptions,
  });

  useHandleCheckoutError(false);

  const cancel = () => {
    setOptionBasket(undefined);
    navigate(`../display?simId=${simId}`, { replace: true });
  };

  const handleSubmit = () => {
    const basketId = optionBasket?.id;
    if (basketId === undefined) {
      captureMessage("Wanted to submit undefined basket id!");
      handleError("Wanted to submit undefined basket id!");

      return;
    }
    if (totalOnceAmount === undefined) {
      captureMessage("Wanted to charge undefined oneTime amount");
      handleError("Wanted to charge undefined oneTime amount");

      return;
    }
    if (!isSubmittingCheckout) {
      if (isInvoice || isCreditCard) {
        setIsSubmittingCheckout(true); // To prevent double checkout on re-rendering
        setFullScreenLoading(
          true,
          t("cockpit.managementTile.roaming.checkout.loading"),
        );
        // Trigger transaction for this basket
        if (isCreditCard) {
          payTransaction({
            params: {
              simId: parseInt(simId),
              basketId: basketId,
              datatransOrderType:
                CreateTransactionDatatransOrderType.AddOptionToContract,
            },
          });
        }
        // this customer is an invoice customer, we just add option to contract
        else if (isInvoice) {
          addContractCallback(basketId);
        }
      } else {
        // Else we create a transaction, which will in turn open the Lightbox, allow the user to enter payment information
        // and call addOptionToContract backend-side if the payment was successful.
        createTransaction({
          params: {
            simId: parseInt(simId),
            basketId: basketId,
            datatransOrderType:
              CreateTransactionDatatransOrderType.AddOptionToContract,
            // TODO: Comment back in during 1393
            // cardholderName,
            ...get3DsBrowserInfo(),
          },
        });
      }
    }
  };

  const addContractCallback = useCallback(
    (basketId: number) => {
      addOptionToContract({
        serviceId: parseInt(simId),
        data: {
          header: getHeader(),
          basket: optionBasket,
          basketId,
        },
      });
    },
    [addOptionToContract, optionBasket, simId],
  );

  // TODO: Comment back in during 1393
  // const [cardholderName, setCardholderName] = useState("");

  const { watch, control } = useForm<FormProps>();
  const watchedPaymentProvider = watch("paymentProvider");
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    usePaymentMode();

  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    !(watchedPaymentProvider || isInvoice || isCreditCard);
  // TODO: Comment back in during 1393
  // || isCardholderNameMissing(watchedPaymentProvider, cardholderName)

  const packagePrice = option?.oneTimeCharges?.[0]?.amount;
  const discountAmount = optionBasket?.total?.oneTimeCampaign?.amount;
  const { campaignEndDate } = useGetCampaignEndDate(optionBasket);
  const credit = getCreditAmount(optionBasket?.balance);
  // substract discount and credit to achieve totalCost
  const totalOnce = (totalOnceAmount ?? 0) - (discountAmount || 0) - credit;
  const currency = getCurrencyFromLookupValue(
    optionBasket?.currency || { id: 1 },
  );

  return (
    <RoamingBookingPage
      dataTestid="checkout-roaming-option"
      title={tCheckout("title")}
      step={2}
      stepLabel={tCheckout("stepLabel")}
      onBack={cancel}
    >
      {isAddingOptionLoading && <LoadingSpinner />}
      {optionBasket && selectedPackage ? (
        <>
          <TwoColumnLayout
            rows={[
              {
                left: (
                  <div className="flex w-full flex-col gap-6">
                    <ProfileCard
                      dataTestid="roaming-checkout-summary"
                      variant="white"
                      bodyGap={6}
                      title={tCheckout("summaryTitle")}
                    >
                      <div className="flex h-14 w-full flex-col justify-center rounded-md bg-surface-quaternary px-5 text-foreground-tertiary">
                        <p className="text-body-xs leading-md">
                          {tCheckout("abo")}
                        </p>
                        <p className="truncate-safe text-body-base font-semibold leading-md">
                          {simDetail != null
                            ? getFlatNameFromDevice(simDetail, false)
                            : ""}
                        </p>
                      </div>

                      <div className="flex w-full flex-col gap-3">
                        <div className="flex w-full items-center gap-3">
                          <p className="truncate-safe min-w-0 flex-1 text-body-base font-semibold leading-none text-foreground-primary">
                            {tCheckout("roamingOption")}
                          </p>
                          <Button
                            dataTestid="roaming-checkout-change"
                            size="sm"
                            variant="secondary"
                            onClick={goToPackageSelection}
                          >
                            {tCheckout("changeRate")}
                          </Button>
                        </div>

                        <SubscriptionInfoBox>
                          <SubscriptionInfoRow
                            icon={<Globe className="size-6" />}
                            title={packageName ?? ""}
                          />
                        </SubscriptionInfoBox>
                      </div>
                    </ProfileCard>

                    <CheckoutPaymentCard
                      isInvoice={isInvoice}
                      isCreditCard={!!isCreditCard}
                      cardType={cardType}
                      maskedCardNumberString={maskedCardNumberString}
                      control={control}
                      sumIsZero={Math.max(totalOnce, 0) === 0}
                    />
                  </div>
                ),
                right: (
                  <ProfileCard
                    dataTestid="roaming-checkout-due"
                    className="self-start"
                    variant="white"
                    title={tCheckout("nowToPay")}
                  >
                    <div className="flex w-full flex-col">
                      <PriceRow
                        withBackdrop
                        variant="strong"
                        label={
                          <>
                            <span>{packageName}</span>
                            {isYearlyPackage && (
                              <>
                                <br />
                                <span className="text-body-xs font-regular leading-md">
                                  {t(
                                    "portal:cockpit.managementTile.roaming.addOption.365days",
                                  )}
                                </span>
                              </>
                            )}
                          </>
                        }
                        value={
                          packagePrice != null
                            ? getCurrency(packagePrice, currency)
                            : ""
                        }
                      />

                      {!!discountAmount && discountAmount > 0 && (
                        <PriceRow
                          withDivider={credit === 0}
                          label={
                            <>
                              {tCheckout("discount")}
                              {campaignEndDate && (
                                <Trans
                                  t={t}
                                  i18nKey="Onboarding.sections.checkout.summary.until_date"
                                  values={{
                                    date: campaignEndDate.toLocaleDateString(
                                      "de-CH",
                                    ),
                                  }}
                                  components={[
                                    <span
                                      key="until_date"
                                      className="text-foreground-error"
                                    />,
                                  ]}
                                />
                              )}
                            </>
                          }
                          value={<DeductionChip amount={discountAmount} />}
                        />
                      )}

                      {credit > 0 && (
                        <PriceRow
                          withDivider
                          label={tCheckout("creditOther")}
                          value={<DeductionChip amount={credit} />}
                        />
                      )}

                      <PriceRow
                        variant="strong"
                        withTopDivider
                        label={
                          <>
                            <span>{tCheckout("sum")}</span>
                            <br />
                            <span className="text-body-xs font-regular leading-md">
                              {t(
                                "Onboarding.sections.checkout.summary.taxIncluded",
                              )}
                            </span>
                          </>
                        }
                        value={
                          <>
                            <span data-testid="roaming-checkout-total">
                              {getCurrency(Math.max(totalOnce, 0), currency)}
                            </span>
                            <span className="text-body-xs font-regular leading-md">
                              {tCheckout("once")}
                            </span>
                          </>
                        }
                      />
                    </div>
                  </ProfileCard>
                ),
              },
            ]}
          />

          <LightboxWrapper
            paymentProvider={watchedPaymentProvider}
            transactionId={datatransRequestDocument?.refNo}
            sign={datatransRequestDocument?.sign}
            enabled={datatransRequestDocument !== undefined}
            // TODO: Comment back in during 1393
            // cardholderName={cardholderName}
            // onCardholderNameChange={setCardholderName}
          />

          <div className="flex w-full justify-between gap-3">
            <Button
              dataTestid="roaming-checkout-back-step"
              variant="secondary"
              onClick={goToPackageSelection}
            >
              {capitalizeFirstLetter(t("common.buttons.back"))}
            </Button>
            <Button
              dataTestid="roaming-checkout-submit"
              disabled={disableSuccessButton}
              onClick={handleSubmit}
            >
              {tCheckout("complete")}
            </Button>
          </div>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </RoamingBookingPage>
  );
};
